import { Tuple2 }		from "ts-base/tuple";
import { Fn }			from "ts-base/fn";
import { Is }			from "ts-base/is";
import { Sets }			from "ts-base/sets";
import { Validator }	from "ts-base/validation/validator";
import * as Q			from "ts-base/validation/query";
import * as J			from "ts-base/validation/json";

export namespace Enum {
	// NOTE this should be readonly, but if it is typescript 5.5.2 sometimes runs into its instantiation limit
	export type Companion<K extends string>	= {
		brand:			Fn<string, K>,

		fromString:		Fn<string, K|null>,
		toString:		Fn<K, string>,

		validateString:	Validator<string, K>,

		is:				(it:string) => it is K,
		value:			(chosen:K)	=> K,

		values:			ReadonlyArray<K>,
		valueSet:		ReadonlySet<K>,

		choose:			<KK extends K>(...chosen:ReadonlyArray<KK>)		=> Subset<K, KK>,
		reject:			<KK extends K>(...rejected:ReadonlyArray<KK>)	=> Subset<K, Exclude<K, KK>>,
		/* first element are the choosen, second element the rejected */
		partition:		<KK extends K>(...chosen:ReadonlyArray<KK>)		=> Tuple2<Subset<K, KK>, Subset<K, Exclude<K, KK>>>,

		query:			Q.ParameterSchema<K>,
		json:			J.Schema<K>,

		flagsToSet:		(flags:Record<K, boolean>)	=> ReadonlyArray<K>,
		setToFlags:		(set:ReadonlyArray<K>)		=> Record<K, boolean>,
	};

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	export type Value<T extends Companion<any>>	= T["values"][number];

	export type Subset<K extends string, KK extends K>	= Readonly<{
		is:			Is<K, KK>,
		value:		(chosen:KK)	=> KK,

		values:		ReadonlyArray<KK>,
		valueSet:	ReadonlySet<KK>,

		asEnum:		()	=> Companion<KK>,
	}>;

	export const of	= <K extends string>(...values:ReadonlyArray<K>):Companion<K>	=> ({
		brand:			(it:string):K		=> it as K,

		fromString:		(it:string):K|null	=> values.includes(it as K) ? it as K : null,
		toString:		(it:K):string		=> it,

		validateString:	Validator.enumValue(values),

		is:				(it:string):it is K => values.includes(it as K),
		value:			(chosen:K):K		=> chosen,

		values:			values,
		valueSet:		Sets.of(values),

		choose:			<KK extends K>(...chosen:ReadonlyArray<KK>)		=> subset(chosen),
		reject:			<KK extends K>(...rejected:ReadonlyArray<KK>)	=> subset(remove(values, rejected)),
		partition:		<KK extends K>(...chosen:ReadonlyArray<KK>)		=> [ subset(chosen), subset(remove(values, chosen)) ],

		query:			Q.enumValue(values),
		json:			J.enumValue(values),

		flagsToSet:	(flags:Record<K, boolean>):ReadonlyArray<K>	=> {
			const out	= [];
			for (const value of values) {
				if (flags[value])	out.push(value);
			}
			return out;
		},
		setToFlags:	(set:ReadonlyArray<K>):Record<K, boolean>	=> {
			const out	= {} as Record<K, boolean>;
			for (const value of values) {
				out[value]	= set.includes(value);
			}
			return out;
		},
	});

	const subset	= <K extends string, KK extends K>(values:ReadonlyArray<KK>):Subset<K, KK>	=> {
		const valueSet:ReadonlySet<KK>	= Sets.of(values);
		return {
			is:		(it:K):it is KK	=> valueSet.has(it as KK),
			value:	(chosen:KK):KK	=> chosen,

			values,
			valueSet,

			asEnum:	() => Enum.of(...values),
		};
	};

	const remove	= <K, KK extends K>(values:ReadonlyArray<K>, rejected:ReadonlyArray<KK>):ReadonlyArray<Exclude<K, KK>>	=>
		values.filter(it => !rejected.includes(it as KK)) as ReadonlyArray<Exclude<K, KK>>;
}
